import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PAGES = {
  '/mentions-legales': {
    title: 'Mentions légales',
    content: (
      <>
        <h2>Éditeur du site</h2>
        <p>SkyApp<br />Logiciel de gestion de chantiers BTP<br />Email : <a href="mailto:contact@skyapp.fr">contact@skyapp.fr</a><br />Téléphone : 06.51.79.27.90</p>

        <h2>Hébergement</h2>
        <p>Le site et l'application SkyApp sont hébergés par :<br /><strong>Render Services, Inc.</strong><br />340 S Lemon Ave #4133, Walnut, CA 91789, États-Unis<br /><a href="https://render.com" target="_blank" rel="noopener noreferrer">https://render.com</a></p>

        <h2>Propriété intellectuelle</h2>
        <p>L'ensemble des contenus présents sur le site SkyApp (textes, images, logos, interface) sont protégés par le droit d'auteur. Toute reproduction, même partielle, est interdite sans autorisation préalable.</p>

        <h2>Données personnelles</h2>
        <p>Les données collectées lors de l'inscription (nom, prénom, email, entreprise) sont utilisées uniquement dans le cadre du service SkyApp. Elles ne sont jamais cédées à des tiers. Conformément au RGPD, vous disposez d'un droit d'accès, de rectification et de suppression en contactant <a href="mailto:contact@skyapp.fr">contact@skyapp.fr</a>.</p>

        <h2>Cookies</h2>
        <p>Le site utilise uniquement des cookies techniques nécessaires au fonctionnement de l'application (authentification, session). Aucun cookie publicitaire ou de tracking n'est utilisé.</p>
      </>
    ),
  },
  '/cgu': {
    title: "Conditions Générales d'Utilisation (CGU)",
    content: (
      <>
        <h2>Article 1 — Objet</h2>
        <p>Les présentes CGU définissent les conditions d'accès et d'utilisation de l'application SkyApp, logiciel de gestion de chantiers destiné aux entreprises du BTP.</p>

        <h2>Article 2 — Accès au service</h2>
        <p>L'accès à SkyApp est réservé aux professionnels disposant d'un compte valide. L'utilisateur s'engage à fournir des informations exactes lors de l'inscription et à ne pas partager ses identifiants.</p>

        <h2>Article 3 — Utilisation du service</h2>
        <p>L'utilisateur s'engage à utiliser SkyApp dans le respect des lois en vigueur et à ne pas :<br />
        – Tenter de contourner les mécanismes de sécurité<br />
        – Utiliser le service à des fins illicites<br />
        – Uploader des contenus illégaux ou portant atteinte à des tiers</p>

        <h2>Article 4 — Disponibilité</h2>
        <p>SkyApp s'efforce d'assurer la disponibilité du service 24h/24, 7j/7, mais ne peut garantir une disponibilité sans interruption. Des maintenances peuvent être effectuées sans préavis.</p>

        <h2>Article 5 — Propriété des données</h2>
        <p>Les données créées par l'utilisateur (chantiers, devis, rapports…) lui appartiennent. SkyApp ne revendique aucun droit sur ces données et ne les exploite pas à des fins commerciales.</p>

        <h2>Article 6 — Résiliation</h2>
        <p>L'utilisateur peut supprimer son compte à tout moment en contactant <a href="mailto:contact@skyapp.fr">contact@skyapp.fr</a>. SkyApp se réserve le droit de suspendre un compte en cas de violation des présentes CGU.</p>

        <h2>Article 7 — Modifications</h2>
        <p>SkyApp se réserve le droit de modifier les présentes CGU à tout moment. L'utilisateur sera informé par email en cas de modification substantielle.</p>

        <h2>Article 8 — Droit applicable</h2>
        <p>Les présentes CGU sont soumises au droit français. Tout litige sera soumis aux tribunaux compétents du ressort de Paris.</p>
      </>
    ),
  },
  '/cgv': {
    title: 'Conditions Générales de Vente (CGV)',
    content: (
      <>
        <h2>Article 1 — Objet</h2>
        <p>Les présentes CGV régissent les relations contractuelles entre SkyApp et tout client souscrivant à un abonnement payant.</p>

        <h2>Article 2 — Tarifs</h2>
        <p>Les tarifs des abonnements sont ceux en vigueur au moment de la souscription, tels qu'affichés sur la page Tarifs du site. Les prix sont indiqués en euros TTC.</p>

        <h2>Article 3 — Modalités de paiement</h2>
        <p>Le paiement s'effectue par carte bancaire via notre prestataire de paiement sécurisé. L'abonnement est renouvelé automatiquement à chaque échéance sauf résiliation préalable.</p>

        <h2>Article 4 — Droit de rétractation</h2>
        <p>Conformément à l'article L221-18 du Code de la consommation, tout client particulier bénéficie d'un délai de rétractation de 14 jours à compter de la souscription. Pour exercer ce droit, contactez <a href="mailto:contact@skyapp.fr">contact@skyapp.fr</a>.</p>

        <h2>Article 5 — Résiliation</h2>
        <p>L'abonnement peut être résilié à tout moment depuis l'espace client ou en contactant notre support. La résiliation prend effet à la fin de la période en cours. Aucun remboursement prorata n'est effectué.</p>

        <h2>Article 6 — Essai gratuit</h2>
        <p>SkyApp propose un essai gratuit de 14 jours sans engagement et sans saisie de carte bancaire. À l'issue de l'essai, l'accès est suspendu si aucun abonnement n'est souscrit.</p>

        <h2>Article 7 — Responsabilité</h2>
        <p>SkyApp ne pourra être tenu responsable de tout préjudice indirect résultant de l'utilisation ou de l'impossibilité d'utiliser le service. La responsabilité de SkyApp est limitée au montant des sommes effectivement versées par le client au cours des 12 derniers mois.</p>

        <h2>Article 8 — Droit applicable</h2>
        <p>Les présentes CGV sont soumises au droit français. Tout litige sera soumis aux tribunaux compétents du ressort de Paris.</p>
      </>
    ),
  },
};

const LegalPage = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const page = PAGES[pathname];

  if (!page) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="text-sm font-medium">Accueil</span>
          </button>
          <span className="text-gray-300">|</span>
          <span className="text-sm font-semibold text-gray-700">{page.title}</span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">{page.title}</h1>
        <div className="prose prose-gray max-w-none space-y-6 text-gray-700 leading-relaxed [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:mt-8 [&_h2]:mb-2 [&_a]:text-blue-600 [&_a]:underline">
          {page.content}
        </div>
        <div className="mt-12 pt-6 border-t border-gray-200 text-sm text-gray-400">
          Dernière mise à jour : juillet 2026 — Pour toute question : <a href="mailto:contact@skyapp.fr" className="text-blue-500 hover:underline">contact@skyapp.fr</a>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
